import { Reveal } from "@/components/Reveal";

// The diagnosis, stated as a descent: what the barn sees, why every system
// starved, and where the knowledge actually went. Each beat sits deeper, and
// one continuous stem connects them, warming toward the orange root below.
const DESCENT = [
  {
    number: "01",
    lead: "The symptom",
    body: "Missed vet dates, lost invoices, the same question asked three times a week. Annoying, survivable, easy to blame on busyness.",
    stem: "bg-cream/30",
  },
  {
    number: "02",
    lead: "The input failure",
    body: "Every system before this one asked the barn to type. The barn was busy with horses, so the database starved and quietly died.",
    stem: "bg-carrot/50",
  },
  {
    number: "03",
    lead: "The access failure",
    body: "What did get recorded lives in one person's head, phone, or notebook. When the horse is sold or the person leaves, the history walks out the door.",
    stem: "bg-carrot",
  },
];

/**
 * Section 3 of the descent: the soil line. The background deepens from leaf
 * green through field green into dark soil. Cream text; the only warmth is
 * the single root fiber leading down to section 4.
 */
export function SoilLine() {
  return (
    <section
      id="why"
      className="soil-field relative overflow-hidden text-cream"
    >
      {/* progressive enhancement: the ground darkens as you scroll through it
          (CSS view() timeline; static gradient is the universal fallback).
          Transparent at the top so the leaf band stays light, and at the
          bottom so there is no seam against the Root section. */}
      <div aria-hidden className="soil-deepen pointer-events-none absolute inset-0" />
      <div className="relative mx-auto max-w-6xl px-5 pb-24 pt-28 sm:px-8 sm:pb-28 sm:pt-32">
        <div className="grid gap-14 lg:grid-cols-[0.78fr_1.22fr] lg:items-start lg:gap-20">
          <Reveal className="max-w-xl">
            <h2 className="text-4xl font-extrabold leading-[1.02] sm:text-5xl">
              The problem isn't on the surface.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-cream/78">
              Two years ago, reading a barn's PDFs, photos, handwriting and voice was a science project.
              Now the mess is finally readable.
            </p>
          </Reveal>

          <div className="soil-steps">
            {DESCENT.map((step, i) => (
              <Reveal key={step.lead} delay={i * 90}>
                <article className={`grid gap-3 py-6 sm:grid-cols-[3rem_1fr] ${i ? "border-t border-cream/12" : "pt-0"}`}>
                  <span className="font-mono text-xs font-bold text-carrot-bright">{step.number}</span>
                  <div className="relative pl-5">
                    {/* the stem grows downward as the step reveals: the
                        diagnosis literally growing toward the root below */}
                    <span aria-hidden className={`soil-stem absolute inset-y-0 left-0 w-0.5 ${step.stem}`} />
                    <h3 className="text-xl font-bold text-cream sm:text-2xl">{step.lead}</h3>
                    <p className="mt-2 max-w-xl leading-relaxed text-cream/70">{step.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
